/**
 * communityMemberGrants — Per-user permission-set holdings on a
 * specific Geo Community. Powers the auto-computed permission badge
 * on Leader cards (POLICY_TEAM_ROSTERS.md §4.3).
 *
 * Backend endpoint: GET /api/permissions/community/{guid}/member-grants
 * Returns one row per (user, bundle) pair. A user holding multiple
 * bundles appears multiple times; the consumer picks the highest-tier
 * one to display.
 */

import { apiFetch } from "./client";

export interface CommunityMemberGrant {
  userGuid: string;
  permissionSetGuid: string;
  permissionSetName: string;
}

/** Fetch all active community-scope grants for a community. */
export async function fetchCommunityMemberGrants(
  communityGuid: string,
): Promise<CommunityMemberGrant[]> {
  return apiFetch<CommunityMemberGrant[]>(
    `/api/permissions/community/${encodeURIComponent(communityGuid)}/member-grants`,
  );
}

/** Tier priority — descending. Higher = more authoritative.
 *  No name shortening: display the full bundle label ("Community Owner",
 *  not "Owner") so it reads as the platform role description it is, not
 *  as a one-word descriptor next to a person's name. */
const TIER_PRIORITY: Array<{ test: (name: string) => boolean; rank: number }> = [
  { test: (n) => /owner/i.test(n),            rank: 100 },
  { test: (n) => /admin/i.test(n),            rank: 80  },
  { test: (n) => /content\s+creator/i.test(n), rank: 60  },
  { test: (n) => /curator/i.test(n),          rank: 50  },
  { test: (n) => /member/i.test(n),           rank: 20  },
  { test: (n) => /viewer/i.test(n),           rank: 10  },
];

/** Pick the badge to display for a user from their list of grants on a
 *  community. Highest-tier wins; ties broken alphabetically.
 *  Returns null if the user has no grants on this community. */
export function pickBadge(grants: CommunityMemberGrant[]): { label: string; tier: number } | null {
  if (grants.length === 0) return null;
  let best: { label: string; tier: number } | null = null;
  for (const g of grants) {
    const tierMatch = TIER_PRIORITY.find((t) => t.test(g.permissionSetName));
    const tier = tierMatch?.rank ?? 0;
    const label = g.permissionSetName;
    if (!best || tier > best.tier || (tier === best.tier && label < best.label)) {
      best = { label, tier };
    }
  }
  return best;
}

/** Group raw grant rows by user_guid (uppercase keys). */
export function groupGrantsByUser(grants: CommunityMemberGrant[]): Map<string, CommunityMemberGrant[]> {
  const out = new Map<string, CommunityMemberGrant[]>();
  for (const g of grants) {
    const key = String(g.userGuid).toUpperCase();
    const list = out.get(key);
    if (list) list.push(g);
    else out.set(key, [g]);
  }
  return out;
}
